import { type ReactNode, useState } from "react"
import { errorToast } from "@/v2-ui/toast"
import Selector, {
  type SelectorTriggerProps,
} from "@/v2-backoffice-client/components/selector"
import { useUserTenantTypeQuery } from "@/v2-backoffice-client/user/tenant/type/user.tenant.type.query"
import { DEFAULT_TENANT_USER_TYPE_CODE } from "@/v2-backoffice-client/tenant/user/tenant.user.constants"

export type UserTenantTypePickerProps = Omit<
  SelectorTriggerProps,
  "children" | "onChange"
> & {
  value: number
  placeholder?: ReactNode
  onChange?(code: number): void
}

function UserTenantTypePicker(props: UserTenantTypePickerProps) {
  const {
    value = DEFAULT_TENANT_USER_TYPE_CODE,
    onChange,
    placeholder = "Select type...",
    ...triggerProps
  } = props

  const [isOpen, setIsOpen] = useState(false)
  const { data, loading, error } = useUserTenantTypeQuery({
    fetchPolicy: "cache-first",
  })

  errorToast(error, {
    title: "Failed to load user team types",
  })

  const userTenantTypes = data?.getUserTenantTypes ?? []
  const selectedType = userTenantTypes.find((type) => {
    return type.Code === value
  })

  return (
    <Selector open={isOpen} onOpenChange={setIsOpen}>
      <Selector.Trigger iconSrc="angle-small-down" {...triggerProps}>
        {selectedType?.Name ?? placeholder}
      </Selector.Trigger>

      <Selector.Content>
        <Selector.List
          emptyLabel="No types found"
          isLoading={loading}
          error={error}
        >
          {userTenantTypes.map((type) => (
            <Selector.Item
              key={type.Code}
              added={type.Code === value}
              isLoading={loading}
              value={String(type.Code)}
              onSelect={() => {
                onChange?.(type.Code)
                setIsOpen(false)
              }}
            >
              <Selector.ItemText
                primary={type.Name}
                secondary={type.Description ?? undefined}
              />
            </Selector.Item>
          ))}
        </Selector.List>
      </Selector.Content>
    </Selector>
  )
}

export default UserTenantTypePicker
